/**
 * web/src/components/shared/Button.tsx
 * Hand-rolled Button matching the design-handoff `.btn` contract.
 * Variants: default (white + 1px border-strong), primary (indigo), danger (red),
 * ghost (transparent). Sizes: sm (4×8 / 12px), default (6×12 / 13px), lg (8×16 / 14px),
 * icon (28×28 square). Supports leading icon via children.
 *
 * Source: planning-docs/design_handoff_pixel_dashboard/design_files/styles.css lines 437-485.
 */
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * cva variant table for Button. Class strings are lifted from `.btn` in
 * design_files/styles.css and translated into Tailwind utilities consuming
 * the @theme tokens (Plan 01).
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 rounded-md border font-medium whitespace-nowrap transition-colors duration-100 focus-visible:outline-2 focus-visible:outline focus-visible:outline-accent focus-visible:outline-offset-1 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default: 'bg-bg border-border-strong text-text hover:bg-bg-hover',
        primary:
          'bg-accent border-accent text-white hover:bg-accent-hover hover:border-accent-hover',
        danger: 'bg-failed border-failed text-white hover:opacity-90',
        ghost: 'bg-transparent border-transparent text-text hover:bg-bg-muted',
      },
      size: {
        sm: 'px-2 py-1 text-xs',
        default: 'px-3 py-1.5 text-[13px]',
        lg: 'px-4 py-2 text-sm',
        icon: 'h-7 w-7 p-1.5',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

/**
 * Public props for Button. Inherits every native button attribute and adds
 * `variant` / `size` from `buttonVariants`.
 */
export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {}

/**
 * Hand-rolled Button primitive used across the dashboard.
 * Default `type="button"` to avoid accidental form submission when placed
 * inside a `<form>` (e.g., RejectModal — see threat T-05-04-04).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
